import { action } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, componentAutoReaction, IDisposable, inject, Title } from 'Mri.Web.Common';
import * as React from 'react';
import { Route, RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { AppStore } from '../Core/AppStore';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplayEdit } from './DisplayEdit';
import { DisplaysStore } from './DisplaysStore';
import { DisplayStatus } from './DisplayStatus';
import { DisplayViewNav } from './DisplayViewNav';
import { LiveChartsView } from './LiveChartsView';
import { MinMaxList } from './MinMaxList';
import {DisplayEventLogList} from "./DisplayEventLogList";

export interface IDisplayViewParams {
    serialNumber: string;
}

export interface IDisplayViewProps extends RouteComponentProps<IDisplayViewParams> {
    onFavoritesClick?(display: ActiveDisplay): void;
    readonly displayStatus: Mri.DisplayStatuses.ActiveDisplayStatus;
}

@autoinject
@observer
export class DisplayView extends React.Component<IDisplayViewProps> {
    @action
    public async componentDidMount() {
        componentAutoReaction(this, () => this.props.match.params.serialNumber, this.loadAsync);
    }

    public async componentWillUnmount() {
        if (this.refreshTimer) window.clearTimeout(this.refreshTimer);
        this.handle?.dispose();
        this.handle = undefined;
    }

    public render() {
        return (
            <>
                {this.renderTitle()}
                <DisplayViewNav serialNumber={this.props.match.params.serialNumber} location={this.props.location} />

                <Route path="/displays/:serialNumber" exact component={DisplayStatus} />
                <Route path="/displays/:serialNumber/edit" exact component={DisplayEdit} />
                <Route path="/displays/:serialNumber/min-max" exact component={MinMaxList} />
                <Route path="/displays/:serialNumber/live-charts" exact component={LiveChartsView} />
                <Route path="/displays/:serialNumber/events" exact component={DisplayEventLogList} />
            </>
        );
    }

    @bind
    private renderTitle() {
        const location = `/displays/${this.props.match.params.serialNumber}`;

        return (
            <Title documentTitle={this.props.match.params.serialNumber}>
                <div className="d-inline-block px-2">
                    <LinkContainer exact to={`${location}`}>
                        <a className="btn btn-link px-0">
                            <h5 className="header-title fw-bold d-inline-block mb-0">
                                {this.props.match.params.serialNumber}
                            </h5>
                        </a>
                    </LinkContainer>
                </div>
            </Title>
        );
    }

    @bind
    @action
    private async loadAsync() {
        if (this.refreshTimer) window.clearTimeout(this.refreshTimer);

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber, {
                params: {
                    status: true,
                },
            });
        } catch (e: any) {
            this.appStore.setError(e);
            return;
        }

        try {
            this.handle = await this.displaysStore.connectionManager.connect();
            await this.displaysStore.subscribeToDisplayAsync(this.props.match.params.serialNumber);
            await this.refresh();
            await this.displaysStore.refreshScreenSnapshotAsync(this.props.match.params.serialNumber);
        } catch (e: any) {
            console.error('SubscribeToDisplay', e);
        }

        if (this.handle) this.refreshTimer = window.setTimeout(this.refresh, 30 * 1000);
    }

    @bind
    private async refresh() {
        if (this.refreshTimer) window.clearTimeout(this.refreshTimer);

        try {
            await this.displaysStore.refresh(this.props.match.params.serialNumber);
        } catch (e: any) {
            console.error(e);
        }

        if (this.handle) this.refreshTimer = window.setTimeout(this.refresh, 30 * 1000);
    }

    private refreshTimer?: number;
    private handle?: IDisposable;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
}
